/** @typedef {{ ID: number, name: string, countryCode: string, flag: string }} CountryOption */

/** @type {Readonly<Array<CountryOption>>} */
export const COUNTRY_OPTIONS = Object.freeze([
  { ID: 1, name: "United States", countryCode: "US", flag: "🇺🇸" },
  { ID: 2, name: "Canada", countryCode: "CA", flag: "🇨🇦" },
  { ID: 3, name: "Australia", countryCode: "AU", flag: "🇦🇺" },
  { ID: 4, name: "United Arab Emirates", countryCode: "AE", flag: "🇦🇪" },
  { ID: 5, name: "United Kingdom", countryCode: "UK", flag: "🇬🇧" },
  { ID: 6, name: "Antigua And Barbuda", countryCode: "AG", flag: "🇦🇬" },
  { ID: 7, name: "Singapore", countryCode: "SG", flag: "🇸🇬" },
  { ID: 8, name: "Bahamas", countryCode: "BS", flag: "🇧🇸" },
  { ID: 9, name: "Mexico", countryCode: "MX", flag: "🇲🇽" },
  { ID: 10, name: "Peru", countryCode: "PE", flag: "🇵🇪" },
]);

/** @type {Readonly<Partial<Record<number, CountryOption>>>} */
export const COUNTRY_OPTIONS_BY_ID = Object.freeze(
  Object.fromEntries(COUNTRY_OPTIONS.map((option) => [option.ID, option]))
);

/**
 * Reverse lookup: ISO country code (e.g. "US") -> numeric country ID.
 * The address-autocomplete kit speaks ISO codes; surfaces that persist a
 * numeric countryID use this to translate the hook's delta back.
 * @type {Readonly<Partial<Record<string, number>>>}
 */
export const COUNTRY_ID_BY_CODE = Object.freeze(
  Object.fromEntries(COUNTRY_OPTIONS.map((option) => [option.countryCode, option.ID]))
);

/**
 * Resolve an ISO country code from a value that may be EITHER a numeric country
 * ID (e.g. `1`, or the string `"1"`) OR an already-ISO code (e.g. `"US"`).
 *
 * Some surfaces persist country ambiguously: the sacrament form stores whatever
 * `CountryField` emits — the numeric ID — while a backend-seeded value arrives
 * as the ISO code. The address-autocomplete kit speaks ISO and gates US-only
 * verification on `country === "US"`, so those surfaces normalize through this
 * at the boundary. Falls back to "US" for empty/unknown input, matching the
 * backend's own default.
 *
 * @param {number|string|null|undefined} idOrCode
 * @returns {string} ISO country code (e.g. "US")
 */
export const countryCodeFromIDOrCode = (idOrCode) =>
  COUNTRY_OPTIONS_BY_ID[idOrCode]?.countryCode ||
  (typeof idOrCode === "string" && idOrCode) ||
  "US";
